import React, { useState } from "react";
import {
  Box,
  TextField,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  InputAdornment,
} from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";
import * as Styled from "./styles";
import { DealerProps } from "store/modules/DealerCare/types.store";
import dealerCareApi from "services/dealerCareApi";
import { toast } from "utils/toast";
import { formatTaxDocument, stripTaxDocumentMask } from "utils/taxDocument";

interface ResellerFilterProps {
  onApplyFilter: (selectedReseller: DealerProps | null) => void;
}

const ResellerFilter: React.FC<ResellerFilterProps> = ({
  onApplyFilter,
}: ResellerFilterProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedReseller, setSelectedReseller] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [filteredDealers, setFilteredDealers] = useState<DealerProps[]>([]);

  const handleGetDealers = async () => {
    setIsLoading(true);

    try {
      const cleanedSearchTerm = stripTaxDocumentMask(searchTerm);

      const response = await dealerCareApi.get(
        `/v1/dealers/search?term=${cleanedSearchTerm}`,
      );
      setFilteredDealers(response.data.data);
    } catch (error) {
      toast("Falha ao carregar as revendas, tente novamente!", {
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyFilter = () => {
    const selected = filteredDealers?.find(
      (dealer: DealerProps) => dealer.id === selectedReseller,
    );
    onApplyFilter(selected as unknown as DealerProps | null);
  };

  return (
    <Styled.FilterContainer>
      <Styled.SearchContainer>
        <TextField
          fullWidth
          label="Buscar por nome ou CNPJ da revenda"
          value={searchTerm || ""}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSearchTerm(e.target.value)
          }
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="inherit" />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
              "& fieldset": {
                borderColor: "#D33100",
              },
            },
          }}
        />
        <Styled.SearchButton onClick={handleGetDealers} disabled={isLoading}>
          BUSCAR
        </Styled.SearchButton>
      </Styled.SearchContainer>

      <Styled.ResultsSection>
        {!!filteredDealers && filteredDealers?.length > 0 && (
          <Typography variant="h6" color="text.secondary">
            Revendas encontradas
          </Typography>
        )}

        <RadioGroup
          value={selectedReseller}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setSelectedReseller(e.target.value)
          }
        >
          {!!filteredDealers &&
            filteredDealers?.length > 0 &&
            filteredDealers?.map((dealer: DealerProps) => (
              <FormControlLabel
                key={dealer.id}
                value={dealer.id}
                control={
                  <Radio
                    sx={{
                      color: "#D33100",
                      "&.Mui-checked": {
                        color: "#D33100",
                      },
                    }}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body1" fontWeight={500}>
                      {dealer.legalName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatTaxDocument(dealer.taxpayerId || "") || "N/A"}
                    </Typography>
                  </Box>
                }
                sx={{
                  alignItems: "flex-start",
                  marginBottom: 1,
                  "& .MuiFormControlLabel-label": {
                    marginLeft: 1,
                  },
                }}
              />
            ))}
        </RadioGroup>
      </Styled.ResultsSection>

      <Styled.ApplyButton
        onClick={handleApplyFilter}
        disabled={!selectedReseller}
        selectedReseller={selectedReseller}
      >
        APLICAR FILTRO
      </Styled.ApplyButton>
    </Styled.FilterContainer>
  );
};

export default ResellerFilter;
